import { useEffect, useState } from "react";
import {
  KeyRound, ShieldAlert, RotateCcw, Package, ShoppingCart, FolderTree, Store,
  Bell, Settings, Users, BarChart3, Megaphone, Activity, CreditCard, Mail, Globe, Archive,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MODULES = [
  { key: "produits", label: "Produits", icon: Package },
  { key: "commandes", label: "Commandes", icon: ShoppingCart },
  { key: "categories", label: "Catégories", icon: FolderTree },
  { key: "magasins", label: "Magasins", icon: Store },
  { key: "restock", label: "Alertes Stock", icon: Bell },
  { key: "reglages", label: "Réglages", icon: Settings },
  { key: "utilisateurs", label: "Utilisateurs", icon: Users, superOnly: true },
  { key: "statistiques", label: "Statistiques", icon: BarChart3, superOnly: true },
  { key: "regie", label: "Régie Publicitaire", icon: Megaphone, superOnly: true },
  { key: "activite", label: "Activité", icon: Activity, superOnly: true },
  { key: "stripe", label: "Paiement Stripe", icon: CreditCard, superOnly: true },
  { key: "communication", label: "Communication", icon: Mail, superOnly: true },
  { key: "seo", label: "SEO", icon: Globe, superOnly: true },
  { key: "sauvegarde", label: "Sauvegarde", icon: Archive, superOnly: true },
];

export default function AdminModules() {
  const { user } = useAuth();
  const [modules, setModules] = useState(null);
  const [counts, setCounts] = useState(null);
  const [saving, setSaving] = useState(null); // key currently saving, for a subtle disabled state

  const load = () => {
    Promise.all([
      api.get("/module-visibility"),
      api.get("/superadmin/users", { params: { role: "" } }),
    ])
      .then(([mv, users]) => { setModules(mv.data.modules); setCounts(users.data.counts); })
      .catch(() => {});
  };

  useEffect(() => { if (user?.is_root) load(); }, [user]);

  if (!user?.is_root) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-copper" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl text-ink">Accès réservé</h1>
        <p className="text-graphite max-w-sm">Ce module est réservé au compte racine — il n&apos;est pas modifiable depuis l&apos;interface, y compris par un autre super administrateur.</p>
      </div>
    );
  }

  const toggle = async (key, role, value) => {
    const next = { ...modules, [key]: { ...modules[key], [role]: value } };
    setModules(next); // optimistic
    setSaving(key);
    try {
      const { data } = await api.put("/root/module-visibility", { modules: { [key]: next[key] } });
      setModules(data.modules);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load(); // revert to server truth on failure
    } finally {
      setSaving(null);
    }
  };

  const reset = async () => {
    if (!window.confirm("Rendre tous les modules visibles pour tous les rôles ?")) return;
    try {
      const { data } = await api.post("/root/module-visibility/reset");
      setModules(data.modules);
      toast.success("Visibilité réinitialisée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-modules">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-gold" /> Compte racine
          </p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Modules & Accès</h1>
          <p className="text-graphite mt-2 max-w-2xl">
            Choisissez quels modules du back-office sont visibles pour les comptes <strong>Admin</strong> et
            <strong> Super Admin</strong>. Le compte racine voit toujours tout, quel que soit ce réglage.
          </p>
        </div>
        <button onClick={reset} className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink px-3 py-2 border border-stone">
          <RotateCcw className="h-3.5 w-3.5" /> Tout réinitialiser
        </button>
      </div>

      {counts && (
        <div className="bg-sand/60 border border-stone rounded-sm p-4 mb-6 text-sm text-graphite flex gap-6">
          <span><strong className="text-ink">{counts.admin}</strong> compte(s) Admin</span>
          <span><strong className="text-ink">{counts.superadmin}</strong> compte(s) Super Admin (hors racine)</span>
        </div>
      )}

      {!modules ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <div className="bg-white border border-stone rounded-sm overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 sm:gap-4 px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-ash border-b border-stone bg-sand/40">
            <span>Module</span>
            <span className="w-16 sm:w-24 text-center">Admin</span>
            <span className="w-16 sm:w-24 text-center">Super Admin</span>
          </div>
          {MODULES.map((m) => {
            const cfg = modules[m.key] || { admin: true, superadmin: true };
            return (
              <div key={m.key} data-testid={`module-row-${m.key}`} className="grid grid-cols-[1fr_auto_auto] gap-2 sm:gap-4 items-center px-5 py-3.5 border-b border-stone last:border-0">
                <span className="flex items-center gap-2.5 text-sm text-ink min-w-0">
                  <m.icon className="h-4 w-4 text-copper shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{m.label}</span>
                </span>
                <span className="w-16 sm:w-24 flex justify-center">
                  {m.superOnly ? (
                    <span className="text-ash text-xs">—</span>
                  ) : (
                    <Switch
                      data-testid={`module-${m.key}-admin`}
                      checked={cfg.admin}
                      disabled={saving === m.key}
                      onCheckedChange={(v) => toggle(m.key, "admin", v)}
                      className="data-[state=checked]:bg-gold"
                    />
                  )}
                </span>
                <span className="w-16 sm:w-24 flex justify-center">
                  <Switch
                    data-testid={`module-${m.key}-superadmin`}
                    checked={cfg.superadmin}
                    disabled={saving === m.key}
                    onCheckedChange={(v) => toggle(m.key, "superadmin", v)}
                    className="data-[state=checked]:bg-gold"
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
